import type { AxiosResponse } from "axios";
import { HTTPBaseService } from "@/consumers/HTTPBaseService";

export interface SupportCenterCapabilities {
  supportCenterEnabled: boolean;
  shellEnabled: boolean;
  paymentDiagnosticsEnabled: boolean;
  requestSubmissionEnabled: boolean;
}

export interface SupportRequestInput {
  category: string;
  subject: string;
  description: string;
  screen: string;
  appTransactionId?: string;
}

export interface SupportRequestAcknowledgement {
  requestId: string;
  message: string;
  isReplay: boolean;
}

export type SupportRequestResult =
  | { status: "accepted"; value: SupportRequestAcknowledgement }
  | {
      status:
        | "invalid"
        | "notFound"
        | "conflict"
        | "mappingMissing"
        | "rateLimited"
        | "unavailable";
    };

export interface PaymentSupportDiagnosis {
  state: "notApplicable" | "pending" | "diagnosed" | "unknown";
  diagnosisCode: string | null;
  title: string | null;
  explanation: string | null;
  action: string | null;
  customerMessage: string | null;
  canQueryStatus: boolean;
}

export type SupportCenterResult<T> =
  | { status: "found"; value: T }
  | { status: "notFound" }
  | { status: "unavailable" };

export class SupportCenterService extends HTTPBaseService {
  constructor() {
    super(import.meta.env.VITE_API_BASE);
  }

  public async getCapabilities(): Promise<
    SupportCenterResult<SupportCenterCapabilities>
  > {
    try {
      const response = await this.instance.get<
        void,
        AxiosResponse<SupportCenterCapabilities>
      >("/Payment/support-diagnostics/capabilities");
      return { status: "found", value: response.data };
    } catch (error) {
      return this.safeFailure(error);
    }
  }

  public async getTransactionDiagnosis(
    appTransactionId: string
  ): Promise<SupportCenterResult<PaymentSupportDiagnosis>> {
    if (!appTransactionId)
      return { status: "notFound" };

    try {
      const response = await this.instance.get<
        void,
        AxiosResponse<PaymentSupportDiagnosis>
      >(
        `/Payment/support-diagnostics/transactions/${encodeURIComponent(
          appTransactionId
        )}`
      );
      return { status: "found", value: response.data };
    } catch (error) {
      return this.safeFailure(error);
    }
  }

  public async submitRequest(
    request: SupportRequestInput,
    idempotencyKey: string
  ): Promise<SupportRequestResult> {
    try {
      const response = await this.instance.post<
        SupportRequestInput,
        AxiosResponse<SupportRequestAcknowledgement>
      >("/Payment/support-center/requests", request, {
        headers: { "Idempotency-Key": idempotencyKey },
      });
      return response.status === 202
        ? { status: "accepted", value: response.data }
        : { status: "unavailable" };
    } catch (error) {
      const status = (error as { Value?: { response?: { status?: number } } })
        ?.Value?.response?.status;
      if (status === 400) return { status: "invalid" };
      if (status === 404) return { status: "notFound" };
      if (status === 409) return { status: "conflict" };
      if (status === 428) return { status: "mappingMissing" };
      if (status === 429) return { status: "rateLimited" };
      return { status: "unavailable" };
    }
  }

  private safeFailure(error: unknown): SupportCenterResult<never> {
    const status = (error as { Value?: { response?: { status?: number } } })
      ?.Value?.response?.status;
    return status === 404 ? { status: "notFound" } : { status: "unavailable" };
  }
}
